import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import { 
  ArrowLeft, TrendingUp, TrendingDown, Users, Heart, MessageSquare,
  Clock, Eye, BarChart3, Target, Sparkles, Calendar, 
  CheckCircle, AlertCircle, Lightbulb, Crown, Activity
} from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const AdvancedAnalytics = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [analytics, setAnalytics] = useState(null);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }

      const response = await axios.get(`${API}/api/premium/advanced-analytics`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setAnalytics(response.data);
      console.log('✅ Advanced analytics loaded:', response.data);
    } catch (err) {
      console.error('❌ Error loading analytics:', err);
      if (err.response?.status === 402) {
        setError('premium_required');
      } else {
        setError('load_failed');
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-rose-500 mx-auto mb-4"></div>
          <p className="text-white text-lg">Loading your analytics...</p>
        </div>
      </div>
    );
  }

  if (error === 'premium_required') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 p-6">
        <div className="max-w-2xl mx-auto text-center py-20">
          <Crown className="w-20 h-20 text-yellow-500 mx-auto mb-6" />
          <h1 className="text-3xl font-bold text-white mb-4">Premium Feature</h1>
          <p className="text-gray-300 mb-8">
            Advanced Analytics requires a Premium or VIP subscription to access detailed insights about your dating performance.
          </p>
          <Button onClick={() => navigate('/subscription/plans')} className="bg-rose-600 hover:bg-rose-700">
            Upgrade Now
          </Button>
        </div>
      </div>
    );
  }

  if (error || !analytics) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 p-6">
        <div className="max-w-2xl mx-auto text-center py-20">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white mb-4">Unable to Load Analytics</h2>
          <p className="text-gray-300 mb-6">There was an error loading your analytics data.</p>
          <Button onClick={fetchAnalytics} className="bg-rose-600 hover:bg-rose-700">
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  const { activity_trends, profile_performance, optimal_activity, conversation_stats, match_quality, insights } = analytics;

  // Find max values for chart scaling
  const maxSessions = Math.max(...activity_trends.map(w => w.sessions), 1);
  const maxMatches = Math.max(...activity_trends.map(w => w.matches), 1);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <Button
            onClick={() => navigate('/premium')}
            variant="outline"
            className="border-white/20 text-white hover:bg-white/10"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
          
          <div className="flex items-center space-x-3">
            <div className="text-center">
              <h1 className="text-2xl font-bold text-white">Advanced Analytics</h1>
              <p className="text-gray-300 text-sm">Deep insights into your dating journey</p>
            </div>
            <Badge className="bg-gradient-to-r from-purple-500 to-pink-500 text-white">
              <BarChart3 className="w-4 h-4 mr-1" />
              Premium
            </Badge>
          </div>
        </div>

        {/* AI Insights Banner */}
        {insights && insights.length > 0 && (
          <Card className="bg-gradient-to-r from-blue-500/20 to-purple-500/20 border-blue-500/30 mb-8">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <Sparkles className="w-8 h-8 text-blue-400 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-white font-semibold text-lg mb-3">AI-Powered Insights</h3>
                  <div className="space-y-2">
                    {insights.map((insight, index) => (
                      <div key={index} className="flex items-start gap-2">
                        {insight.type === 'positive' && <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />}
                        {insight.type === 'tip' && <Lightbulb className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />}
                        {insight.type === 'insight' && <Target className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />}
                        <p className="text-gray-200">{insight.text}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Main Stats Grid */}
        <div className="grid md:grid-cols-4 gap-4 mb-8">
          <Card className="bg-white/5 backdrop-blur-md border-white/10">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-2">
                <Eye className="w-8 h-8 text-blue-400" />
                <span className={`text-sm font-medium flex items-center ${profile_performance.views_trend_percent >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                  {profile_performance.views_trend_percent >= 0 ? <TrendingUp className="w-4 h-4 mr-1" /> : <TrendingDown className="w-4 h-4 mr-1" />}
                  {Math.abs(profile_performance.views_trend_percent)}%
                </span>
              </div>
              <div className="text-3xl font-bold text-white">{profile_performance.views_this_week}</div>
              <p className="text-gray-400 text-sm">Profile Views This Week</p>
            </CardContent>
          </Card>

          <Card className="bg-white/5 backdrop-blur-md border-white/10">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-2">
                <Heart className="w-8 h-8 text-rose-400" />
                <Badge className="bg-rose-500/20 text-rose-400">{profile_performance.match_rate_percent}%</Badge>
              </div>
              <div className="text-3xl font-bold text-white">{profile_performance.total_matches}</div>
              <p className="text-gray-400 text-sm">Total Matches</p>
            </CardContent>
          </Card>

          <Card className="bg-white/5 backdrop-blur-md border-white/10">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-2">
                <MessageSquare className="w-8 h-8 text-purple-400" />
                <Badge className="bg-purple-500/20 text-purple-400">{conversation_stats.response_rate_percent}%</Badge>
              </div>
              <div className="text-3xl font-bold text-white">{conversation_stats.total_messages_sent}</div>
              <p className="text-gray-400 text-sm">Messages Sent</p>
            </CardContent>
          </Card>

          <Card className="bg-white/5 backdrop-blur-md border-white/10">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-2">
                <Target className="w-8 h-8 text-green-400" />
                <Badge className="bg-green-500/20 text-green-400">{match_quality.average_compatibility}%</Badge>
              </div>
              <div className="text-3xl font-bold text-white">{match_quality.total_analyzed}</div>
              <p className="text-gray-400 text-sm">Avg Match Quality</p>
            </CardContent>
          </Card>
        </div>

        <div className="grid lg:grid-cols-2 gap-6 mb-8">
          {/* Activity Trends Chart */}
          <Card className="bg-white/5 backdrop-blur-md border-white/10">
            <CardHeader>
              <CardTitle className="text-white flex items-center">
                <Activity className="w-5 h-5 mr-2 text-blue-400" />
                Activity Trends (Last 8 Weeks)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {activity_trends.map((week, index) => (
                  <div key={index} className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-400">{week.week_start}</span>
                      <span className="text-gray-300">{week.sessions} sessions, {week.matches} matches</span>
                    </div>
                    <div className="flex gap-2 h-6">
                      <div 
                        className="bg-blue-500 rounded-full transition-all duration-500"
                        style={{ width: `${(week.sessions / maxSessions) * 50}%`, minWidth: week.sessions > 0 ? '8px' : '0' }}
                        title={`${week.sessions} sessions`}
                      />
                      <div 
                        className="bg-rose-500 rounded-full transition-all duration-500"
                        style={{ width: `${(week.matches / maxMatches) * 50}%`, minWidth: week.matches > 0 ? '8px' : '0' }}
                        title={`${week.matches} matches`}
                      />
                    </div>
                  </div>
                ))}
                <div className="flex gap-4 mt-4 text-sm">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-blue-500 rounded-full" />
                    <span className="text-gray-400">Sessions</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-rose-500 rounded-full" />
                    <span className="text-gray-400">Matches</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Optimal Activity Hours */}
          <Card className="bg-white/5 backdrop-blur-md border-white/10">
            <CardHeader>
              <CardTitle className="text-white flex items-center">
                <Clock className="w-5 h-5 mr-2 text-yellow-400" />
                Best Times to Be Active
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="mb-6">
                <p className="text-gray-300 mb-4">Your peak performance hours based on match success:</p>
                <div className="flex flex-wrap gap-3">
                  {optimal_activity.peak_hours.map((time, index) => (
                    <Badge 
                      key={index} 
                      className={`px-4 py-2 text-base ${index === 0 ? 'bg-yellow-500/30 text-yellow-300 border-yellow-500/50' : 'bg-white/10 text-white'}`}
                    >
                      {index === 0 && '⭐ '}{time}
                    </Badge>
                  ))}
                </div>
              </div>
              
              <div>
                <p className="text-gray-400 text-sm mb-3">Match distribution by hour:</p>
                <div className="grid grid-cols-12 gap-1">
                  {optimal_activity.hour_distribution.slice(0, 24).map((item, index) => {
                    const maxHourMatches = Math.max(...optimal_activity.hour_distribution.map(h => h.matches), 1);
                    const height = item.matches > 0 ? Math.max((item.matches / maxHourMatches) * 100, 10) : 5;
                    return (
                      <div key={index} className="flex flex-col items-center" title={`${item.hour}: ${item.matches} matches`}>
                        <div 
                          className={`w-full rounded-t transition-all ${item.matches > 0 ? 'bg-gradient-to-t from-rose-600 to-rose-400' : 'bg-white/10'}`}
                          style={{ height: `${height}px` }}
                        />
                        {index % 6 === 0 && (
                          <span className="text-[10px] text-gray-500 mt-1">{item.hour.split(' ')[0]}</span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Match Quality Distribution */}
          <Card className="bg-white/5 backdrop-blur-md border-white/10">
            <CardHeader>
              <CardTitle className="text-white flex items-center">
                <Target className="w-5 h-5 mr-2 text-green-400" />
                Match Quality Distribution
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-center mb-6">
                <div className="text-5xl font-bold text-white mb-2">{match_quality.average_compatibility}%</div>
                <p className="text-gray-400">Average Compatibility Score</p>
              </div>
              
              <div className="space-y-4">
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-green-400">Excellent (80%+)</span>
                    <span className="text-white">{match_quality.score_distribution.excellent}</span>
                  </div>
                  <Progress value={(match_quality.score_distribution.excellent / Math.max(match_quality.total_analyzed, 1)) * 100} className="h-2 bg-white/10" />
                </div>
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-blue-400">Good (60-79%)</span>
                    <span className="text-white">{match_quality.score_distribution.good}</span>
                  </div>
                  <Progress value={(match_quality.score_distribution.good / Math.max(match_quality.total_analyzed, 1)) * 100} className="h-2 bg-white/10" />
                </div>
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-yellow-400">Moderate (40-59%)</span>
                    <span className="text-white">{match_quality.score_distribution.moderate}</span>
                  </div>
                  <Progress value={(match_quality.score_distribution.moderate / Math.max(match_quality.total_analyzed, 1)) * 100} className="h-2 bg-white/10" />
                </div>
                <div>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-gray-400">Low (&lt;40%)</span>
                    <span className="text-white">{match_quality.score_distribution.low}</span>
                  </div>
                  <Progress value={(match_quality.score_distribution.low / Math.max(match_quality.total_analyzed, 1)) * 100} className="h-2 bg-white/10" />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Conversation Stats */}
          <Card className="bg-white/5 backdrop-blur-md border-white/10">
            <CardHeader>
              <CardTitle className="text-white flex items-center">
                <MessageSquare className="w-5 h-5 mr-2 text-purple-400" />
                Conversation Performance
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-6">
                <div className="text-center p-4 bg-white/5 rounded-xl">
                  <div className="text-3xl font-bold text-white mb-1">{conversation_stats.total_messages_sent}</div>
                  <p className="text-gray-400 text-sm">Messages Sent</p>
                </div>
                <div className="text-center p-4 bg-white/5 rounded-xl">
                  <div className="text-3xl font-bold text-white mb-1">{conversation_stats.total_messages_received}</div>
                  <p className="text-gray-400 text-sm">Messages Received</p>
                </div>
                <div className="text-center p-4 bg-white/5 rounded-xl">
                  <div className="text-3xl font-bold text-white mb-1">{conversation_stats.active_conversations}</div>
                  <p className="text-gray-400 text-sm">Active Chats</p>
                </div>
                <div className="text-center p-4 bg-white/5 rounded-xl">
                  <div className="text-3xl font-bold text-purple-400 mb-1">{conversation_stats.response_rate_percent}%</div>
                  <p className="text-gray-400 text-sm">Response Rate</p>
                </div>
              </div>
              
              <div className="mt-6 p-4 bg-purple-500/10 rounded-xl border border-purple-500/20">
                <p className="text-gray-300 text-sm">
                  {conversation_stats.response_rate_percent >= 80 
                    ? "🎉 Great response rate! You're keeping conversations engaging."
                    : conversation_stats.response_rate_percent >= 50
                    ? "👍 Good response rate. Try to reply within 24 hours for better engagement."
                    : "💡 Tip: Faster responses lead to better connections. Try the Conversation Mastery coaching module!"}
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default AdvancedAnalytics;
